/**
 * The route exports of Management ▸ Cycles (package A, 2026-09-30; DEC-87 moved the three
 * sections here from the Cockpit): Status, Findings, Mix (issue 226) and Trend, each over the run in the URL
 * (`?runId=`). With no run named, a file with no row — the run is never guessed (CLAUDE.md §3.4).
 *
 * Each re-reads what its page reads and derives through the page's own pure rules
 * (`cycle-status.ts`, `cycle-findings.ts`, `run-trend.ts`), so the file and the page agree.
 */

import { callOperation } from '@/api';
import { getConsolidationRunDigest, inseasonListRuns, readCycleRuns, runDigest } from '@/api/generated';
import type { ConsolidationRunDigestSummary, CycleRunSummary, InSeasonRunDigest } from '@/api/generated';
import { NOT_PUBLISHED, NO_PRINCIPAL, buildVersion } from '@/export';
import type { ExportCell, ExportColumn, ExportRequest, ProvenanceFilter } from '@/export';

import type { ExportScope } from '../manifest';
import { DIGEST_TOP, familyRows, storeRows } from './cycle-findings';
import { readCycleGrouping } from './cycle-grouping';
import { readCycleModule } from './cycle-module';
import { EXIT_REASON_LABEL_KEY } from './exit-cycle-findings';
import { CYCLE_MODULE_TITLE, CYCLE_MODULES, cycleOf } from './cycle-status';
import { exportDisplayOf, ORCHESTRATOR_STATUS_KEY } from '@/planning/words';
import { TREND_DIRECTORY_LIMIT, cycleOutcome, trendCycles } from './run-trend';
import { readScope } from './scope';

export const CYCLE_STATUS_EXPORT_ID = 'inseason.cycles.status';
export const CYCLE_FINDINGS_EXPORT_ID = 'inseason.cycles.findings';
/** The Findings page with `?module=exit` (issue 226): the exit run's lanes by reason. */
export const EXIT_CYCLE_FINDINGS_EXPORT_ID = 'inseason.cycles.findings.exit';
export const CYCLE_TREND_EXPORT_ID = 'inseason.cycles.trend';
/** The Findings page with `?by=store` (issue 226): the digest's top stores, one table. */
export const STORE_CYCLE_FINDINGS_EXPORT_ID = 'inseason.cycles.findings.stores';
/** «Composizione» (issue 226): the four tiles' rows, one sheet. */
export const CYCLE_MIX_EXPORT_ID = 'inseason.cycles.mix';

const missing: ExportCell = { kind: 'missing', state: 'not-reported' };
const text = (value: string | null | undefined): ExportCell => (value === null || value === undefined || value === '' ? missing : { kind: 'text', value });
const date = (value: string | null | undefined): ExportCell => (value === null || value === undefined || value === '' ? missing : { kind: 'date', iso: value });
const num = (value: number | null | undefined): ExportCell => (value === null || value === undefined ? missing : { kind: 'number', raw: value });

function searchOf(scope?: ExportScope): string {
  return scope?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
}

function runOf(scope?: ExportScope): string | null {
  return readScope(searchOf(scope)).runId;
}

function sheet(
  id: string, titleKey: string, runId: string | null,
  columns: readonly Pick<ExportColumn, 'key' | 'headerKey' | 'kind' | 'display' | 'descriptionKey'>[],
  rows: ExportCell[][], measure: string, definition: string,
): ExportRequest {
  const filters: ProvenanceFilter[] = runId === null ? [] : [{ field: 'run_id', operator: 'equals', value: runId }];
  return {
    id,
    titleKey,
    columns: columns.map((column) => ({ ...column })),
    rows: () => rows,
    rowCount: rows.length,
    provenance: {
      filters,
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: NOT_PUBLISHED,
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [{ measure, definition, registerVersion: '' }],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: buildVersion(),
    },
    perimeter: {
      filters,
      sort: [],
      visibleColumns: columns.map((column) => column.key),
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

async function runSummary(runId: string): Promise<CycleRunSummary | undefined> {
  const page = await callOperation(inseasonListRuns, { query: {} });
  return page.items.find((row) => row.run_id === runId);
}

/** Each cycle's newest tick and schedule, for the run's market — the Status page. */
export async function cycleStatusExport(scope?: ExportScope): Promise<ExportRequest> {
  const runId = runOf(scope);
  const run = runId === null ? undefined : await runSummary(runId);
  const data = run === undefined ? undefined : await callOperation(readCycleRuns, { query: { country_key: run.country_key } });
  const rows: ExportCell[][] = data === undefined ? [] : CYCLE_MODULES.map((module) => {
    const cycle = cycleOf(data, module);
    const orchestrator = cycle?.schedule.orchestrator ?? null;
    return [
      { kind: 'label', value: module, code: module },
      text(cycle?.tick?.cycleTick),
      cycle?.tick === null || cycle?.tick === undefined ? missing : { kind: 'label', value: cycle.tick.state, code: cycle.tick.state },
      orchestrator?.status === null || orchestrator?.status === undefined || orchestrator.status === ''
        ? missing
        : { kind: 'label', value: orchestrator.status, code: orchestrator.status },
      date(orchestrator?.nextTickAt),
      text(cycle?.schedule.declaredCron),
    ];
  });
  return sheet(CYCLE_STATUS_EXPORT_ID, 'inseason.cycles.statusTitle', runId, [
    // The cycle and the orchestrator's status in words in the workbook (audit E2); the CSV keeps the codes.
    { key: 'cycle', headerKey: 'inseason.cycles.column.cycle', descriptionKey: 'inseason.columnGuide.cycles.cycle', kind: 'label', display: exportDisplayOf(CYCLE_MODULE_TITLE) },
    { key: 'cycle_tick', headerKey: 'inseason.cycles.column.tick', descriptionKey: 'inseason.columnGuide.cycles.tick', kind: 'text' },
    { key: 'tick_state', headerKey: 'inseason.cycles.column.tickState', descriptionKey: 'inseason.columnGuide.cycles.tickState', kind: 'label' },
    { key: 'schedule_status', headerKey: 'inseason.cycles.column.scheduleStatus', descriptionKey: 'inseason.columnGuide.cycles.scheduleStatus', kind: 'label', display: exportDisplayOf(ORCHESTRATOR_STATUS_KEY) },
    { key: 'next_tick_at', headerKey: 'inseason.cycles.column.nextTick', descriptionKey: 'inseason.columnGuide.cycles.nextTick', kind: 'date' },
    { key: 'declared_cron', headerKey: 'inseason.cycles.column.cron', descriptionKey: 'inseason.columnGuide.cycles.cron', kind: 'text' },
  ], rows, 'cycle_tick_state', 'Each decision cycle\'s newest tick for the run\'s market as the platform states it (bata-platform ADR 6043), and its schedule as the orchestrator last reported it. Never "succeeded" unless the tick says so.');
}

async function digestOf(runId: string | null): Promise<InSeasonRunDigest | undefined> {
  return runId === null ? undefined : callOperation(runDigest, { path: { run_id: runId }, query: { top: DIGEST_TOP } });
}

/**
 * The Findings page's table, for the module and grouping its URL names (`?module=`, `?by=`, issue
 * 226): the in-season run's digest by department family or by store, or — with `module=exit` — the
 * consolidation run's lanes by the reason that decided them (the grouping is not the exit
 * module's). One route, one export per view; each file names its own id.
 */
export async function cycleFindingsExport(scope?: ExportScope): Promise<ExportRequest> {
  if (readCycleModule(searchOf(scope)) === 'exit') return exitCycleFindingsExport(scope);
  if (readCycleGrouping(searchOf(scope)) === 'store') return storeCycleFindingsExport(scope);
  const runId = runOf(scope);
  const digest = await digestOf(runId);
  const families = digest === undefined ? [] : familyRows(digest) ?? [];
  const rows: ExportCell[][] = families.map((row) => [
    row.family === null ? missing : { kind: 'text', value: row.family },
    num(row.units), num(row.requestedUnits), num(row.unmetUnits), num(row.fillRate),
  ]);
  return sheet(CYCLE_FINDINGS_EXPORT_ID, 'inseason.cycles.findingsTitle', runId, [
    { key: 'department_family', headerKey: 'inseason.found.column.family', descriptionKey: 'inseason.columnGuide.cycles.family', kind: 'text' },
    { key: 'units', headerKey: 'inseason.found.column.units', descriptionKey: 'inseason.columnGuide.cycles.units', kind: 'number' },
    { key: 'requested_units', headerKey: 'inseason.found.column.requested', descriptionKey: 'inseason.columnGuide.cycles.requested', kind: 'number' },
    { key: 'unmet_units', headerKey: 'inseason.found.column.unmet', descriptionKey: 'inseason.columnGuide.cycles.unmet', kind: 'number' },
    { key: 'fill_rate', headerKey: 'inseason.found.inseason.fillRate', descriptionKey: 'inseason.columnGuide.cycles.fillRate', kind: 'number' },
  ], rows, 'proposal_fill_rate', 'Per department family of the run\'s digest (bata-platform ADR 6039): units proposed over units requested, a ratio of sums. Undefined — never zero — when nothing was requested; the unclassified lines are a row of their own with no family.');
}

/**
 * The digest's two store rankings merged (`storeRows`): each store once, its own figures. Only the
 * top {@link DIGEST_TOP} of each ranking are published, which the definition says.
 */
async function storeCycleFindingsExport(scope?: ExportScope): Promise<ExportRequest> {
  const runId = runOf(scope);
  const digest = await digestOf(runId);
  const rows: ExportCell[][] = (digest === undefined ? [] : storeRows(digest)).map((row) => [
    { kind: 'text', value: row.storeKey }, num(row.proposals), num(row.units), num(row.requestedUnits), num(row.unmetUnits),
  ]);
  return sheet(STORE_CYCLE_FINDINGS_EXPORT_ID, 'inseason.cycles.findings.byStore.caption', runId, [
    { key: 'store_key', headerKey: 'inseason.found.column.store', descriptionKey: 'inseason.columnGuide.cycles.storeKey', kind: 'text' },
    { key: 'proposals', headerKey: 'inseason.found.inseason.proposals', descriptionKey: 'inseason.columnGuide.cycles.storeProposals', kind: 'number' },
    { key: 'units', headerKey: 'inseason.found.column.units', descriptionKey: 'inseason.columnGuide.cycles.units', kind: 'number' },
    { key: 'requested_units', headerKey: 'inseason.found.column.requested', descriptionKey: 'inseason.columnGuide.cycles.requested', kind: 'number' },
    { key: 'unmet_units', headerKey: 'inseason.found.column.unmet', descriptionKey: 'inseason.columnGuide.cycles.unmet', kind: 'number' },
  ], rows, 'digest_top_stores', 'The run digest\'s two store rankings (bata-platform ADR 6039) merged, each store once: the top ten by units proposed and the top ten by unmet need (the digest\'s top). The digest publishes no other store, so this is never every store of the run.');
}

/** The kinds of row «Composizione» writes, one per tile — the CSV keeps the code. */
const MIX_KIND_LABEL_KEY: Readonly<Record<string, string>> = {
  department_family: 'inseason.cycles.mix.kind.family',
  reason_code: 'inseason.cycles.mix.kind.reason',
  transfer_purpose: 'inseason.cycles.mix.kind.purpose',
  depth_band: 'inseason.cycles.mix.kind.depth',
};

/**
 * «Composizione»: the four tiles' rows in one sheet, one row per category of each — units and unmet
 * units by department family and by reason code, transfer units by purpose, price steps by depth
 * band (its bounds in points). A figure a kind does not carry is missing, never 0. Each tile still
 * downloads its own CSV from its table view.
 */
export async function cycleMixExport(scope?: ExportScope): Promise<ExportRequest> {
  const runId = runOf(scope);
  const digest = await digestOf(runId);
  const rows: ExportCell[][] = [];
  if (digest !== undefined) {
    const kind = (code: string): ExportCell => ({ kind: 'label', value: code, code });
    const book = digest.proposals;
    for (const group of book.by_department_family.groups ?? []) {
      rows.push([kind('department_family'), { kind: 'text', value: group.key }, num(group.units), num(group.unmet_units), missing]);
    }
    const unclassified = book.by_department_family.unclassified ?? null;
    if (unclassified !== null) rows.push([kind('department_family'), missing, num(unclassified.units), num(unclassified.unmet_units), missing]);
    for (const group of book.by_reason_code) {
      rows.push([kind('reason_code'), { kind: 'text', value: group.key }, num(group.units), num(group.unmet_units), missing]);
    }
    for (const row of digest.transfers.by_purpose) {
      rows.push([kind('transfer_purpose'), { kind: 'text', value: row.purpose }, num(row.units), missing, missing]);
    }
    for (const band of digest.price_steps.by_depth_band) {
      rows.push([kind('depth_band'), { kind: 'text', value: `${band.lower_points}–${band.upper_points}` }, missing, missing, num(band.price_steps)]);
    }
  }
  return sheet(CYCLE_MIX_EXPORT_ID, 'inseason.cycles.mixTitle', runId, [
    { key: 'kind', headerKey: 'inseason.cycles.mix.column.kind', descriptionKey: 'inseason.columnGuide.cycles.mixKind', kind: 'label', display: exportDisplayOf(MIX_KIND_LABEL_KEY) },
    { key: 'category', headerKey: 'inseason.cycles.mix.column.category', descriptionKey: 'inseason.columnGuide.cycles.mixCategory', kind: 'text' },
    { key: 'units', headerKey: 'inseason.found.column.units', descriptionKey: 'inseason.columnGuide.cycles.mixUnits', kind: 'number' },
    { key: 'unmet_units', headerKey: 'inseason.found.column.unmet', descriptionKey: 'inseason.columnGuide.cycles.mixUnmet', kind: 'number' },
    { key: 'price_steps', headerKey: 'inseason.cycles.mix.column.priceSteps', descriptionKey: 'inseason.columnGuide.cycles.mixPriceSteps', kind: 'number' },
  ], rows, 'cycle_digest_mix', 'Per kind of what the run wrote, from its digest (bata-platform ADR 6039): proposed and unmet units by department family (the unclassified lines a row with no family) and by reason code, transfer units by purpose, price steps by depth band in points. A figure a kind does not carry is missing, never 0; money is not here, being per currency.');
}

/**
 * The exit consolidation run's digest, one row per reason code (`lanes.by_reason`): lanes, those
 * proposed, units on all and on the proposed ones, and lanes with no net benefit recorded. Money is
 * left out: the digest publishes it per currency, and one column cannot hold several currencies.
 */
async function exitCycleFindingsExport(scope?: ExportScope): Promise<ExportRequest> {
  const runId = runOf(scope);
  const digest: ConsolidationRunDigestSummary | undefined = runId === null
    ? undefined
    : await callOperation(getConsolidationRunDigest, { path: { run_id: runId } });
  const rows: ExportCell[][] = (digest?.lanes.by_reason ?? []).map((row) => [
    { kind: 'label', value: row.reason_code, code: row.reason_code },
    num(row.lanes), num(row.proposed_lanes), num(row.units), num(row.proposed_units), num(row.lanes_without_net_benefit),
  ]);
  return sheet(EXIT_CYCLE_FINDINGS_EXPORT_ID, 'inseason.cycles.findings.exit.exportTitle', runId, [
    // The reason in words in the workbook; the CSV keeps the code.
    { key: 'reason_code', headerKey: 'inseason.cycles.findings.exit.column.reason', descriptionKey: 'inseason.columnGuide.cycles.exitReason', kind: 'label', display: exportDisplayOf(EXIT_REASON_LABEL_KEY) },
    { key: 'lanes', headerKey: 'inseason.cycles.findings.exit.column.lanes', descriptionKey: 'inseason.columnGuide.cycles.exitLanes', kind: 'number' },
    { key: 'proposed_lanes', headerKey: 'inseason.cycles.findings.exit.column.proposedLanes', descriptionKey: 'inseason.columnGuide.cycles.exitProposedLanes', kind: 'number' },
    { key: 'units', headerKey: 'inseason.cycles.findings.exit.column.units', descriptionKey: 'inseason.columnGuide.cycles.exitUnits', kind: 'number' },
    { key: 'proposed_units', headerKey: 'inseason.cycles.findings.exit.column.proposedUnits', descriptionKey: 'inseason.columnGuide.cycles.exitProposedUnits', kind: 'number' },
    { key: 'lanes_without_net_benefit', headerKey: 'inseason.cycles.findings.exit.column.withoutNetBenefit', descriptionKey: 'inseason.columnGuide.cycles.exitWithoutNetBenefit', kind: 'number' },
  ], rows, 'consolidation_lanes_by_reason', 'Per reason code of the consolidation decision run\'s digest (bata-platform ADR 7023): lanes and units the run stored, and those it proposed; withheld lanes are counted, never dropped. A lane with no net benefit recorded is counted, never valued at 0.');
}

/** The run digests' headline figures cycle by cycle, for the run's market — the Trend page. */
export async function cycleTrendExport(scope?: ExportScope): Promise<ExportRequest> {
  const runId = runOf(scope);
  const run = runId === null ? undefined : await runSummary(runId);
  let rows: ExportCell[][] = [];
  if (run !== undefined) {
    const directory = await callOperation(inseasonListRuns, { query: { country_key: run.country_key, limit: TREND_DIRECTORY_LIMIT } });
    const trend = trendCycles(directory.items, { hasMore: directory.has_more ?? false });
    const digests = new Map<string, InSeasonRunDigest | null>();
    for (const cycle of trend.cycles) {
      for (const member of cycle.runs) {
        try {
          digests.set(member.run_id, await callOperation(runDigest, { path: { run_id: member.run_id }, query: { top: DIGEST_TOP } }));
        } catch {
          digests.set(member.run_id, null);
        }
      }
    }
    rows = trend.cycles.map((cycle) => {
      const outcome = cycleOutcome(cycle, digests);
      const figures = outcome.kind === 'figures' ? outcome.figures : null;
      return [
        text(cycle.key), num(figures?.proposals), num(figures?.units), num(figures?.unmetUnits), num(figures?.fillRate),
        num(figures?.priceSteps),
      ];
    });
  }
  return sheet(CYCLE_TREND_EXPORT_ID, 'inseason.cycles.trendTitle', runId, [
    { key: 'cycle_tick', headerKey: 'inseason.cycles.column.tick', kind: 'text', descriptionKey: 'inseason.columnGuide.cycles.trendTick' },
    { key: 'proposals', headerKey: 'inseason.found.inseason.proposals', kind: 'number', descriptionKey: 'inseason.columnGuide.cycles.trendProposals' },
    { key: 'units', headerKey: 'inseason.found.inseason.units', kind: 'number', descriptionKey: 'inseason.columnGuide.cycles.trendUnits' },
    // The run's digest total, not a family's or a store's: a different definition, so its own header.
    { key: 'unmet_units', headerKey: 'inseason.cycles.column.unmetWholeRun', kind: 'number', descriptionKey: 'inseason.columnGuide.cycleTrend.unmet_units' },
    { key: 'fill_rate', headerKey: 'inseason.found.inseason.fillRate', kind: 'number', descriptionKey: 'inseason.columnGuide.cycles.trendFillRate' },
    { key: 'price_steps', headerKey: 'inseason.cycles.column.priceSteps', kind: 'number', descriptionKey: 'inseason.columnGuide.cycles.trendPriceSteps' },
  ], rows, 'cycle_digest_trend', 'Per cycle tick of the market, the newest run of each entry point and kind (re-executions left out), from each run\'s digest. A cycle whose digest was not read is a gap, never zero.');
}
